import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { fmtSlot, formatTs, slotTimestampMs, type ChainParams } from "../lib/format";
import type { ApiClient } from "../lib/api";
import type { Slot, SlotState, StoredBlock } from "../lib/types";
import { useSseSlots } from "../hooks/useSseSlots";
import { useSseFrames } from "../hooks/useSseFrames";

/**
 * Live multithreaded block DAG.
 *
 * Inspired by `block-explorer-old/massa-graph`, this widget renders a
 * horizontally-scrolling view of recent slots and blocks across every thread.
 *
 * Design choices driven by user feedback:
 *   - Parent links are drawn for ONLY the single most recently produced
 *     block — as soon as a newer block appears, the previous block's fan
 *     disappears instantly. This matches `explorerViewUpdate` in
 *     `massa-graph-old` which only animates `lastblc`.
 *   - Cross-period parent links are uniform cubic-bezier sigmoids so every
 *     arc shares the same shape — no mix of quadratic bows and straight
 *     lines.
 *   - Same-period lower-thread parents EXIT THE LEFT edge of the parent and
 *     loop around, so the visual "weaves" back to the child without
 *     overlapping intermediate block markers (old massa-graph look).
 *   - A periodic slot-range refresh heals stale finalization state even if
 *     the SSE stream misses an update.
 */
// Block bodies are immutable and ids are globally unique hashes, so one
// shared cache across mounts/networks is safe. Pruned by the interval below.
const blockCache = new Map<string, StoredBlock>();
// How long the REST backstop waits for /v1/stream/blocks before fetching a
// block body itself.
const BLOCK_STREAM_GRACE_MS = 5_000;

export function LiveDag({
  client,
  params,
  windowSec = 120,
}: {
  client: ApiClient;
  params: ChainParams;
  windowSec?: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  const slotsRef = useRef<Map<string, SlotState>>(new Map());
  // Module-level so block bodies survive a page change and come back instantly.
  const blocksRef = useRef<Map<string, StoredBlock>>(blockCache);
  // Wall-clock (ms) when we first placed each block in the cache. Doubles as
  // the anchor for the "spawn flash" animation — a block rendered within
  // SPAWN_MS of its firstSeen gets a brighter fill + halo that decays.
  const firstSeenRef = useRef<Map<string, number>>(new Map());
  // The single block whose parent fan is currently being animated. Kept in
  // a ref (not state) to avoid re-rendering on every tick; the 30fps
  // `draw()` loop reads it directly.
  //
  // Invariant: `latestBlockRef.current` is the block with the greatest slot
  // timestamp that has been firstSeen within the last FADE_MS. When a
  // newer block appears, we swap this ref and the previous block's lines
  // disappear on the NEXT frame.
  const latestBlockRef = useRef<{
    id: string;
    block: StoredBlock;
    startedAt: number;
  } | null>(null);
  const latestSeenTsRef = useRef<number>(-Infinity);
  const inflightRef = useRef<Set<string>>(new Set());
  // Failed block fetches (404 not indexed yet, 429, network): retry no sooner
  // than `until`, backing off 5 s → 10 s → … → 60 s. Without this, a failing
  // id was re-requested on every SSE message (~10/s), and under 429 that
  // snowballed into hundreds of req/s.
  const failedRef = useRef<Map<string, { until: number; n: number }>>(new Map());
  // SSE events already applied. The buffer keeps the same objects across
  // renders and only prepends new ones, so this skips the 255 old ones.
  const seenEventsRef = useRef<WeakSet<object>>(new WeakSet());
  // Wall-clock (ms) when a slot we already displayed flipped to final. Drives
  // the blue → green colour fade. Slots that arrive already final (backfill)
  // have no entry and render green straight away.
  const finalAtRef = useRef<Map<string, number>>(new Map());
  const noteFinal = (k: string, prev: SlotState | undefined, next: SlotState) => {
    if (prev && prev.status !== "final" && next.status === "final") {
      finalAtRef.current.set(k, Date.now());
    }
  };
  const [, force] = useState(0);

  // Fetch enough newest-first slot pages to cover the rendered window. The
  // REST endpoint is server-capped at max_page_size=100 (≈ 3 periods ≈ 48 s
  // of history on a 32-thread chain), so a single request can't span the
  // typical 90 s viewport — we follow `cursor_next` until the oldest slot
  // in the page is older than the left edge, or the server runs out.
  const fetchWindow = async (): Promise<SlotState[]> => {
    const oldestEdgeMs = Date.now() - windowSec * 1000 - 2_000;
    const MAX_PAGES = 8;
    const out: SlotState[] = [];
    let cursor: string | null | undefined = null;
    for (let i = 0; i < MAX_PAGES; i++) {
      const env = await api.slotsRange(client, { limit: 100, cursor });
      if (!env.data.length) break;
      out.push(...env.data);
      const oldestTs = slotTimestampMs(
        env.data[env.data.length - 1].slot,
        params,
      );
      if (oldestTs <= oldestEdgeMs) break;
      if (!env.cursor_next) break;
      cursor = env.cursor_next;
    }
    return out;
  };

  const mergeSlotsIntoCache = (rows: SlotState[]): boolean => {
    const m = slotsRef.current;
    let changed = false;
    for (const s of rows) {
      const k = `${s.slot.period}:${s.slot.thread}`;
      const prev = m.get(k);
      if (
        !prev ||
        prev.last_updated_ts_ms !== s.last_updated_ts_ms ||
        prev.status !== s.status ||
        prev.final_block_id !== s.final_block_id
      ) {
        if (!prev || prev.last_updated_ts_ms <= s.last_updated_ts_ms) {
          noteFinal(k, prev, s);
          m.set(k, s);
          changed = true;
        }
      }
    }
    return changed;
  };

  const initial = useQuery({
    queryKey: ["live-dag-initial", windowSec, params.threadCount, params.t0Ms],
    queryFn: fetchWindow,
    refetchOnWindowFocus: false,
    staleTime: 5_000,
  });

  useEffect(() => {
    if (!initial.data) return;
    if (mergeSlotsIntoCache(initial.data)) force((x) => x + 1);
  }, [initial.data]);

  // Periodic range refresh — heals stale finalisation state even if a couple
  // of SSE events got lost on the wire. Same pagination loop as the initial
  // fetch so a reconnect that lost 30+ s of history refills completely.
  useEffect(() => {
    const iv = setInterval(async () => {
      if (document.visibilityState === "hidden") return; // nothing to draw
      try {
        const rows = await fetchWindow();
        if (mergeSlotsIntoCache(rows)) force((x) => x + 1);
      } catch {
        /* transient — next tick will retry */
      }
    }, 4_000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, windowSec, params.threadCount, params.t0Ms]);

  // Shared fetcher: resolves a single block id and, if it's a just-seen
  // block whose slot is newer than anything we've animated before, upgrades
  // it to the currently-animated `latestBlockRef` so its parent fan draws
  // in sync with the spawn flash started by the SSE path below.
  //
  // IMPORTANT: the flash animation is NOT started here — it's started the
  // moment the block id first shows up in a slot row via SSE (see below),
  // so the square appearing, the flash, and the parent fan are all driven
  // by the same tick. This function only *augments* the flash with parent
  // links once the block body is resolved (usually < 100 ms later on LAN).
  const backedOff = (id: string) => {
    const f = failedRef.current.get(id);
    return f !== undefined && Date.now() < f.until;
  };
  const markFailed = (id: string) => {
    const n = (failedRef.current.get(id)?.n ?? 0) + 1;
    const delay = Math.min(60_000, 5_000 * 2 ** (n - 1));
    failedRef.current.set(id, { until: Date.now() + delay, n });
  };

  // Only promote to animated latest when (a) we marked this block as "just
  // spawned" on the slot SSE tick, and (b) its slot is newer than any
  // previously-animated block. Without the firstSeen gate we'd light up every
  // block during the initial backfill. Called when the body arrives AND when
  // the slot tick arrives (whichever comes second has both).
  const promote = (id: string) => {
    const block = blocksRef.current.get(id);
    const spawnAt = firstSeenRef.current.get(id);
    if (!block || !spawnAt) return;
    const slotTs = slotTimestampMs(block.slot, params);
    if (slotTs <= latestSeenTsRef.current) return;
    latestSeenTsRef.current = slotTs;
    // Anchor the fan fade to the SPAWN moment (SSE arrival), not to
    // `Date.now()`, so the fan shares the flash window.
    latestBlockRef.current = { id, block, startedAt: spawnAt };
  };

  // Single entry point for a block body, from /v1/stream/blocks or REST.
  const ingestBlock = (block: StoredBlock) => {
    failedRef.current.delete(block.id);
    blocksRef.current.set(block.id, block);
    promote(block.id);
    force((x) => x + 1);
  };

  // Bodies normally arrive pushed by /v1/stream/blocks (`block_seen` is
  // broadcast before the matching `slot_updated`). This REST fetch is only
  // the fallback for ids the stream missed (reconnect gap, hidden tab).
  useSseFrames<StoredBlock>(client, "/v1/stream/blocks", "block_seen", ingestBlock);

  const fetchBlock = (id: string) => {
    const inflight = inflightRef.current;
    if (inflight.has(id)) return;
    if (blocksRef.current.has(id)) return;
    if (backedOff(id)) return;
    inflight.add(id);
    api
      .block(client, id)
      .then((env) => {
        if (!env?.data) {
          markFailed(id); // 404: not indexed yet
          return;
        }
        ingestBlock(env.data);
      })
      .catch(() => {
        markFailed(id); // 429 / network: the backstop retries after the backoff
      })
      .finally(() => {
        inflight.delete(id);
      });
  };

  // Live updates. When SSE brings us a slot row with a new block id we
  // haven't seen before, we start the spawn-flash animation *now* so the
  // flash is locked to the moment the square appears. The block body comes
  // from /v1/stream/blocks (usually already there); no per-block fetch here.
  const { events } = useSseSlots(client, 256);
  useEffect(() => {
    if (events.length === 0) return;
    const m = slotsRef.current;
    const fs = firstSeenRef.current;
    const now = Date.now();
    let changed = false;
    const seen = seenEventsRef.current;
    for (const e of events) {
      if (seen.has(e)) continue;
      seen.add(e);
      const k = `${e.slot.period}:${e.slot.thread}`;
      const prev = m.get(k);
      if (!prev || prev.last_updated_ts_ms < e.last_updated_ts_ms) {
        noteFinal(k, prev, e);
        m.set(k, e);
        changed = true;
      }
      // Collect every block id the slot references right now. A slot can
      // move through candidate → finalised after its initial appearance,
      // and each new id is treated as its own spawn event.
      const ids = [
        ...(e.candidate_block_ids ?? []),
        ...(e.final_block_id ? [e.final_block_id] : []),
      ];
      const slotTs = slotTimestampMs(e.slot, params);
      // Only flash blocks whose slot is recent. Replayed history (e.g.
      // backfill on reconnect) must not retroactively strobe the canvas.
      const fresh = now - slotTs < 3_000;
      for (const id of ids) {
        if (!id) continue;
        if (!fs.has(id) && fresh) {
          fs.set(id, now);
          changed = true;
          // The body usually arrived first on /v1/stream/blocks: start the
          // parent fan now. If it hasn't, ingestBlock() promotes it later,
          // and the backstop below fetches it over REST if it never comes.
          promote(id);
        }
      }
    }
    if (changed) force((x) => x + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);

  // Bulk prefill: two pages of /v1/blocks (≈ 100 s of chain) so every block
  // in the window has its body (ops count → gold frame) on first paint, and
  // again when the tab becomes visible (the block stream is closed while
  // hidden). 2 requests instead of ~180 one-by-one backstop fetches.
  const prefillingRef = useRef(false);
  useEffect(() => {
    let cancelled = false;
    const prefill = () => {
      if (prefillingRef.current) return;
      prefillingRef.current = true;
      (async () => {
        let cursor: string | null | undefined = null;
        for (let i = 0; i < 2 && !cancelled; i++) {
          const env = await api.recentBlocks(client, 100, cursor);
          for (const b of env.data) blocksRef.current.set(b.id, b);
          force((x) => x + 1);
          if (!env.cursor_next) break;
          cursor = env.cursor_next;
        }
      })()
        .catch(() => {
          /* backstop fetcher covers it */
        })
        .finally(() => {
          prefillingRef.current = false;
        });
    };
    const onVis = () => {
      if (document.visibilityState === "visible") prefill();
    };
    prefill();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [client]);

  // Backstop fetcher — only catches block ids /v1/stream/blocks missed (slots
  // from the periodic slot-range refresh, stream reconnect gaps, hidden tab).
  // Skips fresh slots (BLOCK_STREAM_GRACE_MS) and ids in failure backoff.
  useEffect(() => {
    const CONCURRENCY = 8;
    const iv = setInterval(() => {
      // Hidden tab: the block stream is closed, so every new block would be
      // fetched one by one (~2/s). The prefill on return covers the gap.
      if (document.visibilityState === "hidden") return;
      if (prefillingRef.current) return;
      const inflight = inflightRef.current;
      const blocks = blocksRef.current;
      if (inflight.size >= CONCURRENCY) return;
      const now = Date.now();
      const cutoff = now - windowSec * 1000;

      const todo: { id: string; ts: number }[] = [];
      for (const s of slotsRef.current.values()) {
        const ts = slotTimestampMs(s.slot, params);
        if (ts < cutoff) continue;
        const ids = [
          ...(s.candidate_block_ids ?? []),
          ...(s.final_block_id ? [s.final_block_id] : []),
        ];
        for (const id of ids) {
          if (!id) continue;
          if (blocks.has(id)) continue;
          if (inflight.has(id)) continue;
          if (backedOff(id)) continue; // don't let failing ids hog the slots
          // Fresh slots: give /v1/stream/blocks a few seconds to push the body.
          if (ts > now - BLOCK_STREAM_GRACE_MS) continue;
          todo.push({ id, ts });
        }
      }
      todo.sort((a, b) => b.ts - a.ts);
      for (const { id } of todo.slice(0, CONCURRENCY - inflight.size)) {
        fetchBlock(id);
      }
    }, 1_000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, params, windowSec]);

  // Periodically prune old slots/blocks to bound memory.
  useEffect(() => {
    const iv = setInterval(() => {
      const cutoff = Date.now() - (windowSec + 60) * 1000;
      const sm = slotsRef.current;
      let removed = 0;
      for (const [k, v] of sm) {
        const ts = slotTimestampMs(v.slot, params);
        if (ts < cutoff) {
          sm.delete(k);
          finalAtRef.current.delete(k);
          removed++;
        }
      }
      const bm = blocksRef.current;
      const fs = firstSeenRef.current;
      for (const [id, b] of bm) {
        const ts = slotTimestampMs(b.slot, params);
        if (ts < cutoff) {
          bm.delete(id);
          fs.delete(id);
          removed++;
        }
      }
      // Failures long past their backoff belong to ids that left the window.
      const staleFail = Date.now() - (windowSec + 60) * 1000;
      for (const [id, f] of failedRef.current) {
        if (f.until < staleFail) failedRef.current.delete(id);
      }
      // If the currently-animated latest block aged out, drop it.
      const lb = latestBlockRef.current;
      if (lb && !bm.has(lb.id)) {
        latestBlockRef.current = null;
      }
      if (removed > 0) force((x) => x + 1);
    }, 10_000);
    return () => clearInterval(iv);
  }, [params, windowSec]);

  // Resize observer → keep canvas width matching its container.
  const [width, setWidth] = useState(800);
  useEffect(() => {
    if (!wrapperRef.current) return;
    const el = wrapperRef.current;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  // Hover hit-testing.
  const [hover, setHover] = useState<{
    x: number;
    y: number;
    slot: Slot;
    blockId?: string;
    final?: boolean;
    miss?: boolean;
    ts?: number;
  } | null>(null);
  const hoverRef = useRef<typeof hover>(null);
  hoverRef.current = hover;

  // Draw loop — steady ~30 fps so the timeline scrolls continuously instead
  // of stepping; paused entirely when the tab is hidden.
  useEffect(() => {
    let raf = 0;
    let last = 0;
    const tick = (t: number) => {
      if (document.visibilityState === "hidden") {
        raf = requestAnimationFrame(tick);
        return;
      }
      if (t - last > 33) {
        last = t;
        draw();
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width, params]);

  useEffect(() => {
    draw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });

  function hitTest(clientX: number, clientY: number) {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    return hitPoint(x, y);
  }

  function hitPoint(x: number, y: number) {
    const layout = layoutCache.current;
    if (!layout) return null;
    const { laneHeight, slotSize, threadCount, rightEdgeTs, pxPerMs } = layout;
    // Blocks are drawn at y = (thread + 1) * laneHeight (rails are the
    // gridlines themselves). The inverse mapping is `thread = y/laneHeight
    // - 1`, rounded to the nearest integer. We widen the hit box to
    // include a half-lane above and below so users don't have to pixel-
    // hunt for tiny miss dots (slotSize/3 radius).
    const thread = Math.round(y / laneHeight - 1);
    if (thread < 0 || thread >= threadCount) return null;
    // Generous horizontal tolerance: at least one slot width OR half a
    // period in px, whichever is larger. Makes misses trivially clickable.
    const xTol = Math.max(slotSize * 1.2, laneHeight);
    let bestKey: string | null = null;
    let bestDist = xTol;
    for (const [k, s] of slotsRef.current) {
      if (s.slot.thread !== thread) continue;
      const ts = slotTimestampMs(s.slot, params);
      const sx = (rightEdgeTs - ts) * pxPerMs;
      const d = Math.abs(x - (layout.canvasWidth - sx));
      if (d < bestDist) {
        bestDist = d;
        bestKey = k;
      }
    }
    if (!bestKey) return null;
    const s = slotsRef.current.get(bestKey)!;
    const finalBlock = s.final_block_id ?? null;
    const candidate =
      (s.candidate_block_ids && s.candidate_block_ids[0]) ?? null;
    return {
      x,
      y,
      slot: s.slot,
      blockId: finalBlock ?? candidate ?? undefined,
      final: s.status === "final" && !!finalBlock,
      miss: s.status === "final" && s.is_miss,
      ts: slotTimestampMs(s.slot, params),
    };
  }

  const layoutCache = useRef<{
    canvasWidth: number;
    canvasHeight: number;
    laneHeight: number;
    slotSize: number;
    threadCount: number;
    rightEdgeTs: number;
    pxPerMs: number;
  } | null>(null);

  function draw() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const threadCount = params.threadCount || 32;
    const dpr = window.devicePixelRatio || 1;
    const laneHeight = width < 500 ? 8 : width < 900 ? 10 : 12;
    const canvasHeight = laneHeight * (threadCount + 1);
    // Resizing the backing store reallocates it — only do so on change.
    const bw = Math.floor(width * dpr);
    const bh = Math.floor(canvasHeight * dpr);
    if (canvas.width !== bw || canvas.height !== bh) {
      canvas.width = bw;
      canvas.height = bh;
      canvas.style.height = `${canvasHeight}px`;
      canvas.style.width = `${width}px`;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, canvasHeight);

    const rightEdgeTs = Date.now();
    const leftEdgeTs = rightEdgeTs - windowSec * 1000;
    const pxPerMs = width / (rightEdgeTs - leftEdgeTs);
    const slotSize = laneHeight - 2;

    layoutCache.current = {
      canvasWidth: width,
      canvasHeight,
      laneHeight,
      slotSize,
      threadCount,
      rightEdgeTs,
      pxPerMs,
    };

    // Thread gridlines.
    ctx.lineWidth = 1;
    ctx.strokeStyle = "rgba(148, 163, 184, 0.06)";
    ctx.beginPath();
    for (let i = 0; i < threadCount; i++) {
      const y = (i + 1) * laneHeight;
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
    }
    ctx.stroke();

    // Period boundaries — vertical lines that scroll with time, so the
    // motion of the chain is visible even between block arrivals.
    if (params.t0Ms > 0) {
      const g0 = params.genesisTimestampMs;
      const first = Math.ceil((leftEdgeTs - g0) / params.t0Ms);
      ctx.strokeStyle = "rgba(148, 163, 184, 0.10)";
      ctx.beginPath();
      for (let p = first; g0 + p * params.t0Ms <= rightEdgeTs; p++) {
        const x = Math.round(width - (rightEdgeTs - (g0 + p * params.t0Ms)) * pxPerMs) + 0.5;
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvasHeight);
      }
      ctx.stroke();
    }

    // ---- Layer 1: parent connections -------------------------------------
    // We draw ONE fan — the latest block's. As soon as a newer block
    // claims the `latestBlockRef` ref the previous fan is dropped, exactly
    // like `massa-graph-old`'s single `lastblc` animation.
    const FADE_MS = 1000;
    const bm = blocksRef.current;
    const nowMs = Date.now();
    const latest = latestBlockRef.current;
    const hovered = hoverRef.current?.blockId
      ? bm.get(hoverRef.current.blockId)
      : undefined;
    const fans: { block: StoredBlock; fade: number }[] = [];
    if (latest && nowMs - latest.startedAt < FADE_MS) {
      // Live fan is a brief, subdued hint; the full picture is on hover.
      fans.push({ block: latest.block, fade: 0.5 * (1 - (nowMs - latest.startedAt) / FADE_MS) });
    }
    if (hovered) fans.push({ block: hovered, fade: 1 });

    for (const { block, fade } of fans) {
      const childTs = slotTimestampMs(block.slot, params);
      const cx = width - (rightEdgeTs - childTs) * pxPerMs;
      const cy = (block.slot.thread + 1) * laneHeight;
      const childInRange =
        childTs >= leftEdgeTs - 2000 && childTs <= rightEdgeTs + 2000;
      if (childInRange) {
        for (const pid of block.parents ?? []) {
          const parent = bm.get(pid);
          if (!parent) continue;
          const pts = slotTimestampMs(parent.slot, params);
          if (pts < leftEdgeTs - 5000) continue;
          const px = width - (rightEdgeTs - pts) * pxPerMs;
          const py = (parent.slot.thread + 1) * laneHeight;
          drawParentLink(
            ctx,
            px,
            py,
            cx,
            cy,
            slotSize,
            fade,
            parent.slot.period,
            parent.slot.thread,
            block.slot.period,
            block.slot.thread,
          );
        }
      }
    }

    // ---- Layer 2: slot markers -------------------------------------------
    // When a block first lands in the cache (only for just-produced blocks,
    // not the initial backfill — see the fetcher) we "spawn" it with a
    // brighter fill + a soft halo that decays over SPAWN_MS. This gives a
    // professional-looking arrival pulse without any per-frame cost for
    // older blocks (they take the fast path unchanged).
    const SPAWN_MS = 600;
    const fs = firstSeenRef.current;
    for (const [, s] of slotsRef.current) {
      const ts = slotTimestampMs(s.slot, params);
      if (ts < leftEdgeTs || ts > rightEdgeTs + 2000) continue;
      const x = width - (rightEdgeTs - ts) * pxPerMs;
      const y = (s.slot.thread + 1) * laneHeight;

      const isFinal = s.status === "final";
      const isMiss = isFinal && s.is_miss;
      const hasBlock =
        (s.final_block_id && s.final_block_id.length > 0) ||
        (s.candidate_block_ids && s.candidate_block_ids.length > 0);

      if (isMiss) {
        // Miss dot — opaque gray, distinct from semi-transparent "no-data"
        // empty-slot ticks below. Slightly smaller than a block square so
        // missed slots still read as "different" at a glance.
        ctx.fillStyle = "rgba(148, 163, 184, 0.95)";
        ctx.beginPath();
        ctx.arc(x, y, slotSize / 2.6, 0, Math.PI * 2);
        ctx.fill();
        continue;
      }
      if (!hasBlock) {
        ctx.fillStyle = "rgba(148, 163, 184, 0.25)";
        ctx.beginPath();
        ctx.arc(x, y, slotSize / 4, 0, Math.PI * 2);
        ctx.fill();
        continue;
      }

      const primaryBlockId = s.final_block_id || s.candidate_block_ids?.[0];
      const spawnAt = primaryBlockId ? fs.get(primaryBlockId) : undefined;
      const spawnAge = spawnAt ? nowMs - spawnAt : Infinity;
      const spawning = spawnAge < SPAWN_MS;
      // Candidate blue fades to final green when finality lands live.
      const finalAt = finalAtRef.current.get(`${s.slot.period}:${s.slot.thread}`);
      const f = !isFinal ? 0 : finalAt ? Math.min(1, (nowMs - finalAt) / 800) : 1;
      const ops = primaryBlockId ? bm.get(primaryBlockId)?.operation_ids.length : undefined;
      const fill = `rgb(${lerp(59, 34, f)}, ${lerp(130, 197, f)}, ${lerp(246, 94, f)})`;
      const r = slotSize / 3;

      if (spawning) {
        // Ease-out: starts at 1, ends at 0. Non-linear so the flash fades
        // quickly and then settles to the steady-state look.
        const t = spawnAge / SPAWN_MS;
        const pulse = Math.pow(1 - t, 2);
        // Soft halo (additive white with alpha). Sized slightly larger than
        // the marker and fades along with the pulse.
        const haloR = slotSize * (0.75 + 1.2 * pulse);
        const grad = ctx.createRadialGradient(x, y, 0, x, y, haloR);
        grad.addColorStop(0, `rgba(255, 255, 255, ${(0.55 * pulse).toFixed(3)})`);
        grad.addColorStop(1, "rgba(255, 255, 255, 0)");
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(x, y, haloR, 0, Math.PI * 2);
        ctx.fill();
        // Brighter fill that lerps back to the steady colour.
        ctx.fillStyle = isFinal
          ? `rgba(167, 243, 208, ${(0.4 + 0.6 * (1 - pulse)).toFixed(3)})`
          : `rgba(191, 219, 254, ${(0.4 + 0.6 * (1 - pulse)).toFixed(3)})`;
        const grow = 1 + 0.35 * pulse;
        const g = slotSize * grow;
        roundRect(ctx, x - g / 2, y - g / 2, g, g, r * grow);
        // Steady-state marker drawn on top so the edges settle crisp.
        ctx.fillStyle = fill;
        roundRect(ctx, x - slotSize / 2, y - slotSize / 2, slotSize, slotSize, r);
      } else {
        ctx.fillStyle = fill;
        roundRect(ctx, x - slotSize / 2, y - slotSize / 2, slotSize, slotSize, r);
      }
      if (ops) {
        // "Treasure" frame: fixed gold glow on any block carrying ops.
        ctx.save();
        ctx.shadowColor = "rgba(251, 191, 36, 0.9)";
        ctx.shadowBlur = 6;
        ctx.strokeStyle = "#fcd34d";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.roundRect(x - slotSize / 2, y - slotSize / 2, slotSize, slotSize, r);
        ctx.stroke();
        ctx.restore();
      }
      if (hovered && hovered.id === primaryBlockId) {
        ctx.strokeStyle = "rgba(255, 255, 255, 0.9)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.roundRect(x - slotSize / 2 - 1.5, y - slotSize / 2 - 1.5, slotSize + 3, slotSize + 3, r + 1);
        ctx.stroke();
      }

      const extras = (s.candidate_block_ids?.length ?? 0) - 1;
      if (extras > 0) {
        ctx.fillStyle = "rgba(239, 68, 68, 0.8)";
        roundRect(ctx, x + slotSize / 2 + 1, y - slotSize / 2, Math.max(2, slotSize / 3), slotSize, 1);
      }
    }

    // "Now" cursor on the right edge.
    const glow = ctx.createLinearGradient(width - 24, 0, width, 0);
    glow.addColorStop(0, "rgba(139, 92, 246, 0)");
    glow.addColorStop(1, "rgba(139, 92, 246, 0.18)");
    ctx.fillStyle = glow;
    ctx.fillRect(width - 24, 0, 24, canvasHeight);
    ctx.fillStyle = "rgba(139, 92, 246, 0.9)";
    ctx.fillRect(width - 1, 0, 1, canvasHeight);
  }

  return (
    <div className="card">
      <header className="flex flex-col gap-3 mb-4">
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <h2 className="text-base sm:text-lg font-semibold">
            Live multithreaded DAG
          </h2>
          <span className="text-xs sm:text-sm text-muted">
            Last {windowSec} seconds · hover a block for details, click to open it
          </span>
        </div>
        <div className="text-xs text-muted flex gap-x-4 gap-y-1.5 flex-wrap">
          <LegendDot color="#22c55e" label="final block" />
          <LegendDot color="#3b82f6" label="candidate" />
          <span className="inline-flex items-center gap-1 whitespace-nowrap">
            <span
              className="inline-block w-2.5 h-2.5 rounded-sm"
              style={{ border: "1.5px solid #fcd34d", boxShadow: "0 0 4px rgba(251,191,36,0.9)" }}
            />
            has ops
          </span>
          <LegendDot color="rgba(239,68,68,0.7)" label="fork" />
          <LegendDot color="rgba(148,163,184,0.95)" label="miss" />
          <LegendDot color="rgba(126,158,232,0.85)" label="parents (hover)" />
        </div>
      </header>
      <div ref={wrapperRef} className="relative w-full overflow-hidden rounded-xl bg-dag">
        <canvas
          ref={canvasRef}
          className="w-full block cursor-pointer"
          style={{
            // Blocks fade in from the left edge instead of being clipped.
            maskImage: "linear-gradient(to right, transparent, #000 6%)",
            WebkitMaskImage: "linear-gradient(to right, transparent, #000 6%)",
          }}
          onMouseMove={(e) => {
            const h = hitTest(e.clientX, e.clientY);
            setHover(h);
          }}
          onMouseLeave={() => setHover(null)}
          onClick={(e) => {
            const h = hitTest(e.clientX, e.clientY);
            if (!h) return;
            if (h.blockId) navigate(`/block/${h.blockId}`);
            else navigate(`/slot/${h.slot.period}/${h.slot.thread}`);
          }}
        />
        {hover && (
          <div
            className="pointer-events-none absolute bg-panel2 border border-border rounded-lg px-2.5 py-1.5 text-xs font-mono shadow-xl"
            style={{
              left: Math.min(hover.x + 10, Math.max(0, width - 260)),
              top: Math.max(hover.y - 42, 0),
              whiteSpace: "nowrap",
            }}
          >
            <div>{fmtSlot(hover.slot)}</div>
            {hover.ts != null && (
              <div className="text-muted">{formatTs(hover.ts)}</div>
            )}
            {hover.blockId ? (
              <div className="text-muted">
                {hover.blockId.slice(0, 10)}… · {hover.final ? "final" : "candidate"}
                {blocksRef.current.get(hover.blockId) &&
                  ` · ${blocksRef.current.get(hover.blockId)!.operation_ids.length} ops`}
              </div>
            ) : hover.miss ? (
              <div className="text-muted">miss · click for slot</div>
            ) : null}
          </div>
        )}
      </div>
      <div className="flex justify-between font-mono text-[11px] text-muted/80 mt-2">
        {[3, 2, 1].map((i) => (
          <span key={i}>{Math.round((windowSec * i) / 3)}s</span>
        ))}
        <span>now</span>
      </div>
    </div>
  );
}

/**
 * Draw a single parent→child link.
 *
 * The only three visual cases we care about:
 *
 *   a) Same thread (parent is a previous slot in the child's thread):
 *      cubic bezier bowing ABOVE the thread rail so the curve doesn't
 *      overlap intermediate block markers. Enters the child at its LEFT
 *      edge, leaves the parent from its RIGHT edge (old massa-graph look).
 *
 *   b) Same period, parent on a LOWER thread: the parent is to the LEFT
 *      of the child on the x-axis but we want the line to exit the
 *      parent's LEFT edge and curl around. This yields the nice "weave"
 *      visual from `massa-graph-old` where same-period parents clearly
 *      pre-date the child. Modelled as a cubic bezier whose first
 *      control point is shifted far left of the parent so the curve
 *      swings away from the rails before coming back down to the child's
 *      left edge.
 *
 *   c) Previous-period parent (or any cross-period): UNIFORM horizontal
 *      cubic bezier from parent's RIGHT edge to child's LEFT edge. Every
 *      such link shares the same control-point layout so the fan looks
 *      like a bundle of identical sigmoids rather than a mix of arcs and
 *      straight lines.
 */
function drawParentLink(
  ctx: CanvasRenderingContext2D,
  fromx: number,
  fromy: number,
  tox: number,
  toy: number,
  blockSize: number,
  fade: number,
  parentPeriod: number,
  parentThread: number,
  childPeriod: number,
  childThread: number,
) {
  const a = Math.max(0, Math.min(1, fade));
  if (a <= 0) return;
  ctx.strokeStyle = `rgba(126, 158, 232, ${a.toFixed(3)})`;
  ctx.lineWidth = 1.5;
  ctx.beginPath();

  const endX = tox - blockSize / 2; // child's LEFT edge

  // Case A: same thread (time-chain along the child's rail).
  if (parentThread === childThread) {
    const startX = fromx + blockSize / 2; // parent RIGHT edge
    const dx = endX - startX;
    if (dx <= 0) {
      // Parent overlaps child horizontally — straight fallback.
      ctx.moveTo(fromx, fromy);
      ctx.lineTo(tox, toy);
      ctx.stroke();
      return;
    }
    // Bow upward, but clamp the arc so it never rises into the previous
    // thread's row (which would visually cross neighbouring blocks — see
    // explorer feedback about "the last two threads crossing"). Block
    // centers are `laneHeight` apart and each block is `blockSize` tall;
    // the gap between block edges of consecutive threads is therefore
    // `laneHeight - blockSize`. We give the bow at most ~70% of that gap.
    const laneHeight = blockSize + 2;
    const maxBow = Math.max(2, Math.floor((laneHeight - blockSize) * 0.7));
    const bow = Math.min(maxBow, Math.max(blockSize * 0.2, dx * 0.04));
    const bowY = fromy - bow;
    ctx.moveTo(startX, fromy);
    ctx.bezierCurveTo(
      startX + dx * 0.33,
      bowY,
      startX + dx * 0.67,
      bowY,
      endX,
      toy,
    );
    ctx.stroke();
    return;
  }

  // Case B: same period, parent on a lower thread — exit parent LEFT edge
  // and loop back under/over to the child's LEFT edge.
  if (parentPeriod === childPeriod && parentThread < childThread) {
    const startX = fromx - blockSize / 2; // parent LEFT edge
    const dx = endX - startX;
    const swing = Math.max(blockSize * 2, Math.abs(dx) * 0.8);
    // First control point: far left of the parent, pulled slightly up so
    // the curve arches away from the rails.
    const c1x = startX - swing;
    const c1y = fromy;
    // Second control point: just left of the child, at the child's rail
    // level so we enter horizontally.
    const c2x = endX - Math.max(blockSize * 2, dx * 0.3);
    const c2y = toy;
    ctx.moveTo(startX, fromy);
    ctx.bezierCurveTo(c1x, c1y, c2x, c2y, endX, toy);
    ctx.stroke();
    return;
  }

  // Case C: cross-period — uniform sigmoid. Parent RIGHT → child LEFT
  // with symmetric horizontal control points. Every cross-period parent
  // of the same child therefore renders with an identical bend.
  const startX = fromx + blockSize / 2;
  const dx = endX - startX;
  const mid = Math.max(blockSize * 2, Math.abs(dx) * 0.5);
  ctx.moveTo(startX, fromy);
  ctx.bezierCurveTo(startX + mid, fromy, endX - mid, toy, endX, toy);
  ctx.stroke();
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      <span
        className="inline-block w-2.5 h-2.5 rounded-sm"
        style={{ background: color }}
      />
      {label}
    </span>
  );
}

function lerp(a: number, b: number, t: number): number {
  return Math.round(a + (b - a) * t);
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, r);
  ctx.fill();
}
